import React from 'react';
import { Typography, Paper, Box, Button, Divider, Chip, LinearProgress } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { Obra } from '../../services/types';
import { capitalizeFirstLetter, formatCurrency } from '../../utils/formatters';
import {
    formatDate,
    calculateElapsedTime,
    calculateTimePassed,
    getDeadlineInfo,
    getTimeProgressPercent,
} from '../../utils/dateUtils';

interface ObraHeaderProps {
    obra: Obra;
    isAdmin?: boolean;
    onEdit: () => void;
    onDelete: () => void;
}

const getStatusChipColor = (status: string): 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status) {
        case 'Em Andamento':
            return 'primary';
        case 'Concluída':
        case 'Finalizada':
            return 'success';
        case 'Pausada':
            return 'warning';
        case 'Planejada':
            return 'info';
        default:
            return 'default';
    }
};

export const ObraHeader: React.FC<ObraHeaderProps> = ({ obra, isAdmin = false, onEdit, onDelete }) => {
    const deadline = getDeadlineInfo(obra.data_termino_prevista, obra.status);
    const timeProgress = getTimeProgressPercent(obra.data_inicio, obra.data_termino_prevista);
    const unidadesList = Array.isArray(obra.unidades) ? obra.unidades : [];
    const responsaveisList = Array.isArray(obra.responsaveis) ? obra.responsaveis : [];
    const qtdUnidades = unidadesList.length > 0 ? unidadesList.length : Number(obra.quantidade_unidades) || 0;
    const vgvEstimado =
        unidadesList.length > 0
            ? unidadesList.reduce((acc, u) => acc + (Number(u.valor) || 0), 0)
            : (Number(obra.quantidade_unidades) || 0) * (Number(obra.valor_venda_unidade) || 0);
    const areaConstruidaTotal = unidadesList.reduce((acc, u) => acc + (Number(u.area_construida) || 0), 0);
    const areaPrivativaTotal = unidadesList.reduce(
        (acc, u) =>
            acc +
            (Number(
                u.area_privativa_total ??
                    u.area ??
                    ((Number(u.area_construida) || 0) + (Number(u.area_nao_construida) || 0) || 0)
            ) || 0),
        0
    );
    const totalVagas = unidadesList.reduce((acc, u) => acc + (Number(u.vagas) || 0), 0);

    const docs = [
        { label: 'CNO', value: obra.cno?.trim() },
        { label: 'Inscrição Municipal', value: obra.inscricao_municipal?.trim() },
        { label: 'Matrícula do Imóvel', value: obra.matricula_imovel?.trim() },
        { label: 'Alvará', value: obra.numero_alvara?.trim() },
    ].filter((doc) => Boolean(doc.value));

    const enderecoCompleto = [
        obra.endereco?.trim(),
        [obra.municipio?.trim(), obra.estado?.trim()].filter(Boolean).join(' - '),
    ]
        .filter(Boolean)
        .join(', ');

    return (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            {/* Linha Principal: Nome + Status e Ações */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 2,
                    mb: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        {obra.nome}
                    </Typography>
                    <Chip
                        label={obra.status}
                        color={getStatusChipColor(obra.status)}
                        sx={{ fontWeight: 600 }}
                    />
                    {deadline.isOverdue && (
                        <Chip
                            icon={<WarningAmberRoundedIcon />}
                            label={deadline.label}
                            color="error"
                            variant="outlined"
                            size="small"
                            sx={{ fontWeight: 600 }}
                        />
                    )}
                </Box>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button variant="outlined" startIcon={<EditIcon />} onClick={onEdit}>
                        Editar Obra
                    </Button>
                    {isAdmin && (
                        <Button variant="contained" color="error" startIcon={<DeleteIcon />} onClick={onDelete}>
                            Excluir Obra
                        </Button>
                    )}
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Blocos de Informações: Localização/Projeto e Prazos */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 3,
                }}
            >
                {/* Bloco 1: Localização, Responsáveis e Projeto */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                        <LocationOnOutlinedIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Localização, Responsáveis e Projeto
                        </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        <strong>Endereço:</strong> {enderecoCompleto || 'Não informado'}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Responsável(is):</strong>
                        </Typography>
                        {responsaveisList.length > 0 ? (
                            responsaveisList.map((resp) => (
                                <Chip
                                    key={resp.id}
                                    icon={<PersonOutlineIcon />}
                                    label={
                                        resp.percentual_participacao != null
                                            ? `${resp.nome} (${Number(resp.percentual_participacao).toFixed(0)}%)`
                                            : resp.nome
                                    }
                                    size="small"
                                    color="secondary"
                                    variant="outlined"
                                    sx={{ fontWeight: 500 }}
                                />
                            ))
                        ) : (
                            <Typography variant="body2" color="text.secondary">
                                Não definido
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                        <Chip
                            icon={<HomeWorkOutlinedIcon />}
                            label={`Tipo: ${capitalizeFirstLetter(obra.tipo_de_construcao || 'Não definido')}`}
                            size="small"
                            variant="outlined"
                        />
                        {qtdUnidades > 0 && (
                            <Chip
                                label={`${qtdUnidades} ${qtdUnidades === 1 ? 'Unidade' : 'Unidades'}`}
                                size="small"
                                variant="outlined"
                            />
                        )}
                        {areaConstruidaTotal > 0 && (
                            <Chip
                                label={`Área Construída: ${areaConstruidaTotal.toLocaleString('pt-BR')} m²`}
                                size="small"
                                variant="outlined"
                            />
                        )}
                        {areaPrivativaTotal > 0 && (
                            <Chip
                                label={`Área Privativa Total: ${areaPrivativaTotal.toLocaleString('pt-BR')} m²`}
                                size="small"
                                variant="outlined"
                            />
                        )}
                        {totalVagas > 0 && (
                            <Chip
                                label={`${totalVagas} ${totalVagas === 1 ? 'Vaga' : 'Vagas'}`}
                                size="small"
                                variant="outlined"
                            />
                        )}
                        {vgvEstimado > 0 && (
                            <Chip
                                label={`VGV Estimado: ${formatCurrency(vgvEstimado)}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                        )}
                    </Box>
                </Box>

                {/* Bloco 2: Prazos e Andamento Temporal */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.primary' }}>
                        <EventOutlinedIcon fontSize="small" color="primary" />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            Prazos e Cronograma Geral
                        </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                        <strong>Início:</strong> {formatDate(obra.data_inicio)} &nbsp;|&nbsp;{' '}
                        <strong>Término Previsto:</strong> {formatDate(obra.data_termino_prevista)}
                        {obra.data_limite_correcao && (
                            <>
                                {' '}&nbsp;|&nbsp; <strong>Limite Correção:</strong> {formatDate(obra.data_limite_correcao)}
                            </>
                        )}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        <strong>Duração Prevista:</strong> {calculateElapsedTime(obra.data_inicio, obra.data_termino_prevista)} &nbsp;|&nbsp;{' '}
                        <strong>Tempo Transcorrido:</strong> {calculateTimePassed(obra.data_inicio, obra.data_termino_prevista)}
                    </Typography>

                    {obra.data_inicio && obra.data_termino_prevista && (
                        <Box sx={{ mt: 0.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Progresso do Prazo
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 600,
                                        color: deadline.isOverdue ? 'error.main' : 'text.secondary',
                                    }}
                                >
                                    {deadline.isOverdue ? deadline.label : `${timeProgress.toFixed(0)}%`}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={timeProgress}
                                color={deadline.isOverdue ? 'error' : obra.status === 'Concluída' ? 'success' : 'primary'}
                                sx={{ height: 6, borderRadius: 3 }}
                            />
                        </Box>
                    )}
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Bloco 3: Documentação Legal */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <DescriptionOutlinedIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Documentação:
                    </Typography>
                </Box>

                {docs.length > 0 ? (
                    docs.map((doc) => (
                        <Chip
                            key={doc.label}
                            label={`${doc.label}: ${doc.value}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                        />
                    ))
                ) : (
                    <Typography variant="caption" color="text.secondary">
                        Nenhum documento legal informado (CNO, Alvará, Matrícula ou Inscrição Municipal)
                    </Typography>
                )}
            </Box>
        </Paper>
    );
};
